import { useRef } from 'react';
import type { PointerEvent } from 'react';

type SwipeNavigationOptions = {
  count: number;
  currentIndex: number;
  enabled?: boolean;
  onChange: (nextIndex: number) => void;
  threshold?: number;
};

type SwipeStart = {
  x: number;
  y: number;
  pointerId: number;
};

export function useSwipeNavigation({
  count,
  currentIndex,
  enabled = true,
  onChange,
  threshold = 42,
}: SwipeNavigationOptions) {
  const startRef = useRef<SwipeStart | null>(null);

  const active = enabled && count > 1;

  const clampIndex = (index: number) => Math.max(0, Math.min(count - 1, index));

  const finishSwipe = (event: PointerEvent<HTMLElement>) => {
    const start = startRef.current;
    startRef.current = null;
    if (!active || !start || start.pointerId !== event.pointerId) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.2) return;

    const nextIndex = clampIndex(currentIndex + (dx < 0 ? 1 : -1));
    if (nextIndex !== currentIndex) {
      onChange(nextIndex);
    }
  };

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (!active) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      startRef.current = {
        x: event.clientX,
        y: event.clientY,
        pointerId: event.pointerId,
      };
    },
    onPointerUp: finishSwipe,
    onPointerCancel: () => {
      startRef.current = null;
    },
  };
}
